import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import ApprovalIcon from '@mui/icons-material/Approval';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../../context/Auth0Context';
import { useAppContext } from '../../context/AppContext';
import RoleManagement from './RoleManagement';

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newPermissions, setNewPermissions] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentTab, setCurrentTab] = useState(0);

  // Enhanced admin features state
  const [auditTrail, setAuditTrail] = useState([]);
  const [systemStats, setSystemStats] = useState({
    totalProjects: 0,
    activeWPS: 0,
    pendingApprovals: 0,
    systemHealth: 'good'
  });
  const [auditFilters, setAuditFilters] = useState({
    user_id: '',
    project_id: '',
    start_date: '',
    end_date: '',
    revision_type: ''
  });
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);

  // WPS Approvals state
  const [wpsEntries, setWpsEntries] = useState([]);
  const [wpsFilters, setWpsFilters] = useState({
    user_id: '',
    project_id: '',
    department_id: '',
    start_date: '',
    end_date: ''
  });
  const [selectedWpsEntry, setSelectedWpsEntry] = useState(null);
  const [wpsActionDialog, setWpsActionDialog] = useState(false);
  const [deleteWpsDialog, setDeleteWpsDialog] = useState(false);
  const [wpsToDelete, setWpsToDelete] = useState(null);
  const [editWpsDialog, setEditWpsDialog] = useState(false);
  const [wpsToEdit, setWpsToEdit] = useState(null);
  const [editWpsData, setEditWpsData] = useState({});

  const { user: currentUser, isAdmin } = useAuth();
  const { refreshDashboardData } = useAppContext();

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/';
      return;
    }
  }, [isAdmin]);

  // Fetch users and enhanced admin data
  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchDepartments();
    fetchSystemStats();
    fetchAuditTrail();
    fetchWpsEntries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users', { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      showSnackbar('Failed to fetch users', 'error');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects', { withCredentials: true });
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/api/departments', { withCredentials: true });
      setDepartments(response.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchSystemStats = async () => {
    try {
      // Fetch system statistics
      const projectsRes = await axios.get('/api/projects', { withCredentials: true });

      setSystemStats({
        totalProjects: projectsRes.data.length
      });
    } catch (err) {
      console.error('Error fetching system stats:', err);
    }
  };

  const fetchAuditTrail = async () => {
    try {
      const response = await axios.get('/api/reports/revision-history', {
        params: auditFilters,
        withCredentials: true
      });
      setAuditTrail(response.data.revisions || []);
    } catch (err) {
      console.error('Error fetching audit trail:', err);
      setAuditTrail([]); // Set empty array on error to prevent map error
    }
  };

  const fetchWpsEntries = async () => {
    try {
      // Build query parameters object, excluding empty values
      const queryParams = {};
      
      if (wpsFilters.user_id) {
        queryParams.user_id = wpsFilters.user_id;
      }
      if (wpsFilters.project_id) {
        queryParams.project_id = wpsFilters.project_id;
      }
      if (wpsFilters.department_id) {
        queryParams.department_id = wpsFilters.department_id;
      }
      if (wpsFilters.start_date) {
        queryParams.start_date = wpsFilters.start_date;
      }
      if (wpsFilters.end_date) {
        queryParams.end_date = wpsFilters.end_date;
      }

      const response = await axios.get('/api/planned-hours', {
        params: queryParams,
        withCredentials: true
      });

      // The backend now returns entries with joined user, project, and task names
      setWpsEntries(response.data);
    } catch (err) {
      console.error('Error fetching WPS entries:', err);
      setWpsEntries([]);
    }
  };

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setNewRole('');
    setNewPermissions('');
  };

  const handleRoleChange = async () => {
    if (!selectedUser || !newRole || !newPermissions) return;

    try {
      // Use the proper role assignment API that updates both Supabase and Auth0
      const response = await axios.post('/api/admin/assign-role', {
        userId: selectedUser.auth0_user_id || selectedUser.id,
        role: newRole,
        permissions: newPermissions,
        employeeId: selectedUser.id
      }, { withCredentials: true });

      if (response.data.success) {
        // Update local state
        setUsers(users.map(user =>
          user.id === selectedUser.id
            ? { ...user, role: newRole, permissions: newPermissions }
            : user
        ));

        showSnackbar(`Successfully updated ${selectedUser.name}'s role and permissions`, 'success');
        showSnackbar('Settings updated! User must log out and log back in to see changes.', 'info');
        handleCloseDialog();
      }
    } catch (err) {
      console.error('Error updating user role and permissions:', err);
      showSnackbar('Failed to update user settings', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    console.log('🔍 Tab changed to:', newValue);
    setCurrentTab(newValue);
  };

  const handleAuditFilterChange = (field, value) => {
    setAuditFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyAuditFilters = () => {
    fetchAuditTrail();
  };

  const clearAuditFilters = () => {
    setAuditFilters({
      user_id: '',
      project_id: '',
      start_date: '',
      end_date: '',
      revision_type: ''
    });
    setTimeout(() => fetchAuditTrail(), 100);
  };

  const handleWpsFilterChange = (field, value) => {
    setWpsFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyWpsFilters = () => {
    fetchWpsEntries();
  };

  const clearWpsFilters = () => {
    setWpsFilters({
      user_id: '',
      project_id: '',
      department_id: '',
      start_date: '',
      end_date: ''
    });
    setTimeout(() => fetchWpsEntries(), 100);
  };

  const handleWpsView = (entry) => {
    console.log('Viewing WPS entry:', entry);
    setSelectedWpsEntry(entry);
    setWpsActionDialog(true);
  };

  const handleWpsEdit = (entry) => {
    console.log('WPS Entry for editing:', entry); // Debug log to see available fields

    setWpsToEdit(entry);

    // If actual_hours_normal and actual_hours_outside_normal are not available,
    // use actual_duration for normal hours and 0 for outside hours as default
    const actualHoursNormal = entry.actual_hours_normal !== undefined && entry.actual_hours_normal !== null
      ? entry.actual_hours_normal
      : (entry.actual_duration || 0);
    const actualHoursOutside = entry.actual_hours_outside_normal !== undefined && entry.actual_hours_outside_normal !== null
      ? entry.actual_hours_outside_normal
      : 0;

    setEditWpsData({
      project_id: entry.project_id || '',
      user_id: entry.user_id || '',
      task_id: entry.task_id || null,
      hours: entry.hours || 0,
      hours_outside_normal: entry.hours_outside_normal || 0,
      week_start: entry.week_start || '',
      job_number: entry.job_number || '',
      project_lead: entry.project_lead || '',
      actual_duration: entry.actual_duration || 0,
      actual_hours_normal: actualHoursNormal,
      actual_hours_outside_normal: actualHoursOutside,
      comments: entry.comments || '',
      start_date: entry.start_date || '',
      end_date: entry.end_date || '',
      status: entry.status || 'draft', // Default to 'draft' for consistency
      project_stage_id: entry.project_stage_id || null,
      job_code_id: entry.job_code_id || null
    });
    setEditWpsDialog(true);
  };

  const handleWpsDelete = async (entry) => {
    setWpsToDelete(entry);
    setDeleteWpsDialog(true);
  };

  const confirmWpsDelete = async () => {
    try {
      await axios.delete(`/api/planned-hours/${wpsToDelete.id}`, { withCredentials: true });
      showSnackbar('WPS entry deleted successfully', 'success');
      
      // Refresh the WPS entries list
      fetchWpsEntries();
      
      // Refresh dashboard data in AppContext so changes show up immediately in dashboard
      try {
        await refreshDashboardData();
        console.log('✅ Dashboard data refreshed after WPS deletion');
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
        // Don't fail the whole operation if dashboard refresh fails
      }
      
      setDeleteWpsDialog(false);
      setWpsToDelete(null);
    } catch (error) {
      console.error('Error deleting WPS entry:', error);
      showSnackbar('Failed to delete WPS entry', 'error');
    }
  };

  const handleSaveWpsEdit = async () => {
    try {
      await axios.put(`/api/planned-hours/admin/${wpsToEdit.id}`, editWpsData, {
        withCredentials: true
      });
      
      showSnackbar('WPS entry updated successfully', 'success');
      
      // Refresh the WPS entries list
      fetchWpsEntries();
      
      // Refresh dashboard data
      try {
        await refreshDashboardData();
        console.log('✅ Dashboard data refreshed after WPS edit');
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
      }
      
      setEditWpsDialog(false);
      setWpsToEdit(null);
      setEditWpsData({});
    } catch (error) {
      console.error('Error updating WPS entry:', error);
      showSnackbar(
        error.response?.data?.error || 'Failed to update WPS entry', 
        'error'
      );
    }
  };

  const cancelWpsEdit = () => {
    setEditWpsDialog(false);
    setWpsToEdit(null);
    setEditWpsData({});
  };

  const cancelWpsDelete = () => {
    setDeleteWpsDialog(false);
    setWpsToDelete(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'administrator':
      case 'admin':
        return 'error';
      case 'Director':
        return 'error';
      case 'Manager':
        return 'warning';
      case 'Professional Project Manager':
      case 'Professional Quantity Surveyor':
        return 'warning';
      case 'Senior Project Manager':
      case 'Senior Quantity Surveyor':
        return 'info';
      case 'Intermediate Project Manager':
      case 'Intermediate Quantity Surveyor':
        return 'primary';
      case 'Junior Project Manager':
      case 'Junior Quantity Surveyor':
        return 'secondary';
      case 'Trainee Project Manager':
      case 'Trainee Quantity Surveyor':
        return 'default';
      case 'Project Administrator':
      case 'General Administration':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'administrator':
      case 'admin':
        return <SecurityIcon fontSize="small" />;
      case 'Director':
        return <SecurityIcon fontSize="small" />;
      case 'Manager':
        return <AdminPanelSettingsIcon fontSize="small" />;
      case 'Professional Project Manager':
      case 'Senior Project Manager':
      case 'Intermediate Project Manager':
      case 'Junior Project Manager':
      case 'Trainee Project Manager':
        return <AdminPanelSettingsIcon fontSize="small" />;
      case 'Professional Quantity Surveyor':
      case 'Senior Quantity Surveyor':
      case 'Intermediate Quantity Surveyor':
      case 'Junior Quantity Surveyor':
      case 'Trainee Quantity Surveyor':
        return <PersonIcon fontSize="small" />;
      case 'Project Administrator':
      case 'General Administration':
        return <AdminPanelSettingsIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  if (!isAdmin()) {
    return null; // Will redirect in useEffect
  }

  // Debug logging
  console.log('🔍 AdminPage rendering - isAdmin:', isAdmin(), 'currentTab:', currentTab);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#2D3A45', fontWeight: 600 }}>
          System Administration
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#5A6A75' }}>
          Comprehensive system management and monitoring
        </Typography>
      </Box>

      {/* Tabbed Interface */}
      <Paper sx={{ borderRadius: '16px', boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}
        >
          <Tab
            icon={<PersonIcon />}
            label="User Management"
            iconPosition="start"
          />
          <Tab
            icon={<ApprovalIcon />}
            label="WPS"
            iconPosition="start"
          />
          <Tab
            icon={<HistoryIcon />}
            label="Audit Trail"
            iconPosition="start"
          />
          <Tab
            icon={<SecurityIcon />}
            label="Role Management"
            iconPosition="start"
          />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {/* Tab 0: User Management */}
          {currentTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3A45', mb: 2 }}>
                User Role Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage user roles and permissions
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Current Role</TableCell>
                      <TableCell>Hourly Rate</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getRoleIcon(user.role)}
                            {user.name}
                            {user.id === currentUser?.id && (
                              <Chip label="You" size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            color={getRoleColor(user.role)}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>R{user.hourly_rate?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleOpenDialog(user)}
                            size="small"
                            disabled={user.id === currentUser?.id} // Prevent self-modification
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 1: WPS Approvals */}
          {currentTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3A45', mb: 2 }}>
                Work Planning Schedule Approvals
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Review and approve WPS entries from all users
              </Typography>

              {/* WPS Filters */}
              <Paper sx={{
                p: 3,
                mb: 4,
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="h6" sx={{
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontWeight: 600,
                  color: '#2D3A45'
                }}>
                  <FilterListIcon fontSize="medium" />
                  Filter WPS Entries
                </Typography>
                <Grid container spacing={3} sx={{ width: '100%' }}>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <FormControl fullWidth>
                      <InputLabel>User</InputLabel>
                      <Select
                        value={wpsFilters.user_id}
                        label="User"
                        onChange={(e) => handleWpsFilterChange('user_id', e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">All Users</MenuItem>
                        {users.map(user => (
                          <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={wpsFilters.project_id}
                        label="Project"
                        onChange={(e) => handleWpsFilterChange('project_id', e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">All Projects</MenuItem>
                        {projects.map(project => (
                          <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={wpsFilters.department_id}
                        label="Department"
                        onChange={(e) => handleWpsFilterChange('department_id', e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">All Departments</MenuItem>
                        {departments.map(department => (
                          <MenuItem key={department.id} value={department.id}>{department.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 1.8 }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={wpsFilters.start_date}
                      onChange={(e) => handleWpsFilterChange('start_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 1.8 }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={wpsFilters.end_date}
                      onChange={(e) => handleWpsFilterChange('end_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 1.2 }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'row', lg: 'column' },
                      gap: 1.5,
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', lg: 'center' }
                    }}>
                      <Button
                        variant="contained"
                        onClick={applyWpsFilters}
                        sx={{
                          minWidth: '70px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: { xs: 1, lg: 'none' },
                          maxWidth: { xs: '120px', lg: 'none' }
                        }}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={clearWpsFilters}
                        sx={{
                          minWidth: '70px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: { xs: 1, lg: 'none' },
                          maxWidth: { xs: '120px', lg: 'none' }
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* WPS Entries Table */}
              <TableContainer sx={{
                maxHeight: '600px',
                overflowX: 'auto',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                  width: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555',
                }
              }}>
                <Table size="small" sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Project</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Job Number</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Project Stage</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Department</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Job Code</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Normal Hours</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Outside Normal Hours</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Project Lead</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Revision</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Created</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wpsEntries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.user_name || 'Unknown User'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.project_name || 'Unknown Project'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.project_job_number || entry.job_number || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {entry.project_stage_name
                            ? `${entry.project_stage_number}. ${entry.project_stage_name}`
                            : '-'
                          }
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.department_name || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {entry.job_code
                            ? `${entry.job_code}: ${entry.job_code_description}`
                            : '-'
                          }
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{Number(entry.hours || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{Number(entry.hours_outside_normal || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.project_lead || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>#{entry.current_revision || 1}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(entry.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleWpsView(entry)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Entry (Admin Override)">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleWpsEdit(entry)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Entry">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleWpsDelete(entry)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {wpsEntries.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    No WPS entries found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No WPS entries match the current filter criteria.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 2: Audit Trail */}
          {currentTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3A45', mb: 2 }}>
                Comprehensive Audit Trail
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Track all WPS revisions and system changes
              </Typography>

              {/* Audit Filters */}
              <Paper sx={{
                p: 3,
                mb: 4,
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="h6" sx={{
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontWeight: 600,
                  color: '#2D3A45'
                }}>
                  <FilterListIcon fontSize="medium" />
                  Filter Audit Trail
                </Typography>
                <Grid container spacing={3} sx={{ width: '100%' }}>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <FormControl fullWidth>
                      <InputLabel>User</InputLabel>
                      <Select
                        value={auditFilters.user_id}
                        label="User"
                        onChange={(e) => handleAuditFilterChange('user_id', e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">All Users</MenuItem>
                        {users.map(user => (
                          <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={auditFilters.project_id}
                        label="Project"
                        onChange={(e) => handleAuditFilterChange('project_id', e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">All Projects</MenuItem>
                        {projects.map(project => (
                          <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={auditFilters.start_date}
                      onChange={(e) => handleAuditFilterChange('start_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={auditFilters.end_date}
                      onChange={(e) => handleAuditFilterChange('end_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 2.4 }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'row', lg: 'row' },
                      gap: 1.5,
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', lg: 'flex-start' }
                    }}>
                      <Button
                        variant="contained"
                        onClick={applyAuditFilters}
                        sx={{
                          minWidth: '80px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: { xs: 1, lg: 'none' },
                          maxWidth: { xs: '120px', lg: 'none' }
                        }}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={clearAuditFilters}
                        sx={{
                          minWidth: '80px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: { xs: 1, lg: 'none' },
                          maxWidth: { xs: '120px', lg: 'none' }
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Audit Trail Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell>Revision</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditTrail.map((entry) => (
                      <TableRow key={`${entry.entry_type}-${entry.id}`} hover>
                        <TableCell>
                          {new Date(entry.revision_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{entry.user_name}</TableCell>
                        <TableCell>{entry.project_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={entry.activity || entry.revision_type || 'Unknown'}
                            size="small"
                            color={entry.activity?.includes('Created') || entry.revision_type === 'create' ? 'success' :
                                   entry.activity?.includes('Updated') || entry.revision_type === 'update' ? 'warning' : 'info'}
                          />
                        </TableCell>
                        <TableCell>#{entry.revision_number}</TableCell>
                        <TableCell>{entry.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 3: Role Management */}
          {currentTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                🔧 Role Management Debug
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                If you can see this message, the tab is working. Loading RoleManagement component...
              </Typography>
              <RoleManagement />
            </Box>
          )}

        </Box>
      </Paper>

      {/* Role Change Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change User Role & Permissions
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Update settings for: <strong>{selectedUser?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Current role: {selectedUser?.role} | Current permissions: {selectedUser?.permissions === 'admin' ? 'Administrator' : 'Standard User'}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Job Role</InputLabel>
              <Select
                value={newRole}
                label="Job Role"
                onChange={(e) => setNewRole(e.target.value)}
              >
                <MenuItem value="Trainee Project Manager">Trainee Project Manager</MenuItem>
                <MenuItem value="Trainee Quantity Surveyor">Trainee Quantity Surveyor</MenuItem>
                <MenuItem value="Junior Project Manager">Junior Project Manager</MenuItem>
                <MenuItem value="Junior Quantity Surveyor">Junior Quantity Surveyor</MenuItem>
                <MenuItem value="Intermediate Project Manager">Intermediate Project Manager</MenuItem>
                <MenuItem value="Intermediate Quantity Surveyor">Intermediate Quantity Surveyor</MenuItem>
                <MenuItem value="Senior Project Manager">Senior Project Manager</MenuItem>
                <MenuItem value="Senior Quantity Surveyor">Senior Quantity Surveyor</MenuItem>
                <MenuItem value="Professional Project Manager">Professional Project Manager</MenuItem>
                <MenuItem value="Professional Quantity Surveyor">Professional Quantity Surveyor</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Director">Director</MenuItem>
                <MenuItem value="Project Administrator">Project Administrator</MenuItem>
                <MenuItem value="General Administration">General Administration</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>System Permissions</InputLabel>
              <Select
                value={newPermissions}
                label="System Permissions"
                onChange={(e) => setNewPermissions(e.target.value)}
              >
                <MenuItem value="user">Standard User Access</MenuItem>
                <MenuItem value="admin">Administrator Access</MenuItem>
              </Select>
            </FormControl>

            {newPermissions === 'admin' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Administrator permissions grant full system access including user management.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleRoleChange}
            variant="contained"
            disabled={!newRole || !newPermissions || (newRole === selectedUser?.role && newPermissions === selectedUser?.permissions)}
          >
            Update Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* WPS View Dialog - Enhanced Styling */}
      <Dialog
        open={wpsActionDialog}
        onClose={() => setWpsActionDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.20)',
            minHeight: '70vh',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <VisibilityIcon sx={{ fontSize: 24 }} />
          <Typography variant="h5" fontWeight="600">
            WPS Entry Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: 'transparent' }}>
          <Box sx={{ p: 4 }}>
            {selectedWpsEntry && (
              <Grid container spacing={3}>
                {/* User Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 2 }}>
                      User Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          USER
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.user_name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          PROJECT LEAD
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.project_lead || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Project Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 2 }}>
                      Project Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          PROJECT
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.project_name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          JOB NUMBER
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.project_job_number || selectedWpsEntry.job_number || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Hours Information */}
                <Grid size={{ xs: 12 }}>
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 3 }}>
                      Hours Information
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 3 }}>
                      <Box sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        p: 2,
                        flex: 1,
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          PLANNED HOURS
                        </Typography>
                        <Typography variant="h5" fontWeight="700" color="primary.main">
                          {Number(selectedWpsEntry.hours).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        p: 2,
                        flex: 1,
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          ACTUAL NORMAL HOURS
                        </Typography>
                        <Typography variant="h5" fontWeight="700" color="primary.main">
                          {selectedWpsEntry.actual_hours_normal ? Number(selectedWpsEntry.actual_hours_normal).toFixed(2) : '0.00'}
                        </Typography>
                      </Box>
                      <Box sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        p: 2,
                        flex: 1,
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          ACTUAL OUTSIDE HOURS
                        </Typography>
                        <Typography variant="h5" fontWeight="700" color="primary.main">
                          {selectedWpsEntry.actual_hours_outside_normal ? Number(selectedWpsEntry.actual_hours_outside_normal).toFixed(2) : '0.00'}
                        </Typography>
                      </Box>
                      {selectedWpsEntry.hours_outside_normal > 0 && (
                        <Box sx={{
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          p: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            OUTSIDE NORMAL
                          </Typography>
                          <Typography variant="h5" fontWeight="700" color="primary.main">
                            {Number(selectedWpsEntry.hours_outside_normal).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Classification Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 2 }}>
                      Classification
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          PROJECT STAGE
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.project_stage_name
                            ? `${selectedWpsEntry.project_stage_number}. ${selectedWpsEntry.project_stage_name}`
                            : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          DEPARTMENT
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.department_name || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          JOB CODE
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {selectedWpsEntry.job_code
                            ? `${selectedWpsEntry.job_code}: ${selectedWpsEntry.job_code_description}`
                            : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Status Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 2 }}>
                      Status Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          STATUS
                        </Typography>
                        <Chip
                          label={selectedWpsEntry.status || 'Draft'}
                          color={selectedWpsEntry.status === 'completed' ? 'success' : 'default'}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          REVISION
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          #{selectedWpsEntry.current_revision || 1}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          CREATED
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {new Date(selectedWpsEntry.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Activity Period */}
                {selectedWpsEntry.start_date && selectedWpsEntry.end_date && (
                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 3 }}>
                        Activity Period
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 3 }}>
                        <Box sx={{
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          p: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            START DATE
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="primary.main">
                            {new Date(selectedWpsEntry.start_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box sx={{
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          p: 2,
                          flex: 1,
                          textAlign: 'center',
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            END DATE
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="primary.main">
                            {new Date(selectedWpsEntry.end_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Comments */}
                {selectedWpsEntry.comments && (
                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ mb: 2 }}>
                        Comments
                      </Typography>
                      <Box sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {selectedWpsEntry.comments}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setWpsActionDialog(false)}
            variant="contained"
            sx={{ px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* WPS Delete Confirmation Dialog */}
      <Dialog open={deleteWpsDialog} onClose={cancelWpsDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delete WPS Entry
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this WPS entry?
            </Typography>
            {wpsToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>User:</strong> {wpsToDelete.user_name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Project:</strong> {wpsToDelete.project_name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Hours:</strong> {wpsToDelete.hours}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Created:</strong> {new Date(wpsToDelete.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelWpsDelete}>Cancel</Button>
          <Button 
            onClick={confirmWpsDelete} 
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* WPS Edit Dialog */}
      <Dialog open={editWpsDialog} onClose={cancelWpsEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit WPS Entry (Administrator Override)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {wpsToEdit && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    As an administrator, you can edit this WPS entry regardless of edit restrictions.
                    Changes will create a new revision and update associated timesheet entries.
                  </Alert>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>User</InputLabel>
                    <Select
                      value={editWpsData.user_id}
                      label="User"
                      onChange={(e) => setEditWpsData(prev => ({ ...prev, user_id: e.target.value }))}
                    >
                      {users.map(user => (
                        <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={editWpsData.project_id}
                      label="Project"
                      onChange={(e) => setEditWpsData(prev => ({ ...prev, project_id: e.target.value }))}
                    >
                      {projects.map(project => (
                        <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Planned Hours (Normal)"
                    type="number"
                    value={editWpsData.hours === 0 ? '' : editWpsData.hours}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, hours: parseFloat(e.target.value) || 0 }))}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder="0"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Planned Hours (Outside Normal)"
                    type="number"
                    value={editWpsData.hours_outside_normal === 0 ? '' : editWpsData.hours_outside_normal}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, hours_outside_normal: parseFloat(e.target.value) || 0 }))}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder="0"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Actual Hours (Normal)"
                    type="number"
                    value={editWpsData.actual_hours_normal === 0 ? '' : editWpsData.actual_hours_normal}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, actual_hours_normal: parseFloat(e.target.value) || 0 }))}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder="0"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Actual Hours (Outside Normal)"
                    type="number"
                    value={editWpsData.actual_hours_outside_normal === 0 ? '' : editWpsData.actual_hours_outside_normal}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, actual_hours_outside_normal: parseFloat(e.target.value) || 0 }))}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder="0"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Job Number"
                    value={editWpsData.job_number}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, job_number: e.target.value }))}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Project Lead"
                    value={editWpsData.project_lead}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, project_lead: e.target.value }))}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={editWpsData.start_date}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, start_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={editWpsData.end_date}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, end_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Comments"
                    multiline
                    rows={3}
                    value={editWpsData.comments}
                    onChange={(e) => setEditWpsData(prev => ({ ...prev, comments: e.target.value }))}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Total Planned Hours:</strong> {(editWpsData.hours + editWpsData.hours_outside_normal).toFixed(1)}h
                    <br />
                    <strong>Total Actual Hours:</strong> {(editWpsData.actual_hours_normal + editWpsData.actual_hours_outside_normal).toFixed(1)}h
                    <br />
                    <strong>Variance:</strong> {((editWpsData.hours + editWpsData.hours_outside_normal) - (editWpsData.actual_hours_normal + editWpsData.actual_hours_outside_normal)).toFixed(1)}h
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelWpsEdit}>Cancel</Button>
          <Button 
            onClick={handleSaveWpsEdit} 
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPage;
